// Anonymous (pre-login) cookie consent, stored locally. Accounts that never
// signed in can't persist preferences server-side, yet advertising pixels on
// accounts.prospero.food need an explicit marketing choice for GDPR. Categories
// mirror the website banner (analitici / marketing); logged-in users are
// authoritative through MARKETING/ANONYMIZED_STATISTICS prefs instead.

const STORAGE_KEY = 'prospero_app_anon_consent';
const MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

export interface AnonymousConsent {
  analytics: boolean;
  marketing: boolean;
  savedAt: string;
}

export function readAnonymousConsent(): AnonymousConsent | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AnonymousConsent>;
    if (
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.marketing !== 'boolean' ||
      typeof parsed.savedAt !== 'string'
    ) {
      return null;
    }
    // Expired entries behave like "never asked" so the banner can re-prompt.
    if (
      Number.isNaN(Date.parse(parsed.savedAt)) ||
      Date.now() - Date.parse(parsed.savedAt) > MAX_AGE_MS
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { analytics: parsed.analytics, marketing: parsed.marketing, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function writeAnonymousConsent(analytics: boolean, marketing: boolean): AnonymousConsent {
  const consent: AnonymousConsent = {
    analytics,
    marketing,
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage unavailable / quota — the in-memory value still drives this session.
  }
  return consent;
}
